import {
	Bell,
	BellRing,
	Check,
	CheckCheck,
	GitMerge,
	MessageSquare,
	X,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { formatProjectName, safeHttpUrl } from "../utils";

const describeChange = (change) => {
	if (change.type === "comments") {
		return {
			Icon: MessageSquare,
			text: `${change.count} new comment${change.count > 1 ? "s" : ""}`,
		};
	}

	return { Icon: GitMerge, text: "Merged" };
};

const hasMergeActivity = (items) =>
	items.some((item) =>
		item.activity.some(
			(change) => change.type === "state" && change.to === "merged",
		),
	);

const NotificationLink = ({ mr, onOpen }) => {
	const href = safeHttpUrl(mr.webUrl);
	const className =
		"block truncate text-sm font-semibold text-[var(--color-link)] hover:underline";

	if (!href) {
		return (
			<span className={className}>
				#{mr.iid}: {mr.title}
			</span>
		);
	}

	return (
		<a
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			onClick={onOpen}
			className={className}
		>
			#{mr.iid}: {mr.title}
		</a>
	);
};

export const NotificationBell = ({ items, onMarkSeen, onMarkAllSeen }) => {
	const [open, setOpen] = useState(false);
	const popoverRef = useRef(null);
	const count = items.length;
	const active = count > 0;
	const hasMerged = hasMergeActivity(items);
	const Icon = active ? BellRing : Bell;

	useEffect(() => {
		if (!open) return undefined;

		const handleMouseDown = (event) => {
			if (popoverRef.current?.contains(event.target)) return;
			setOpen(false);
		};
		const handleKeyDown = (event) => {
			if (event.key === "Escape") setOpen(false);
		};

		document.addEventListener("mousedown", handleMouseDown);
		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("mousedown", handleMouseDown);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [open]);

	return (
		<div ref={popoverRef} className="relative">
			<button
				type="button"
				onClick={() => setOpen((value) => !value)}
				aria-label={
					active
						? `${count} watched merge request notification${count > 1 ? "s" : ""}`
						: "No watched merge request notifications"
				}
				aria-expanded={open}
				title="Notifications"
				className={`relative flex h-8 w-8 items-center justify-center rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-opacity-50 ${
					active
						? "bg-[var(--color-accent-muted)] text-[var(--color-accent)]"
						: "text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)]"
				} ${hasMerged ? "ring-2 ring-[var(--color-selection-ring)]" : ""}`}
			>
				<Icon className="h-4 w-4" />
				{active && (
					<span className="absolute -right-1 -top-1 flex min-w-4 items-center justify-center rounded-full bg-[var(--color-action)] px-1 text-[10px] font-semibold leading-4 text-[var(--color-action-text)]">
						{count > 9 ? "9+" : count}
					</span>
				)}
			</button>

			{open && (
				<div className="absolute right-0 top-full z-50 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)] shadow-xl">
					<div className="flex items-center justify-between gap-2 border-b border-[var(--color-border)] px-3 py-2">
						<p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
							Notifications
						</p>
						<div className="flex items-center gap-1">
							{active && (
								<button
									type="button"
									onClick={() => onMarkAllSeen?.(items.map((item) => item.mr))}
									aria-label="Mark all notifications as seen"
									title="Mark all as seen"
									className="flex h-7 w-7 items-center justify-center rounded text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
								>
									<CheckCheck className="h-4 w-4" />
								</button>
							)}
							<button
								type="button"
								onClick={() => setOpen(false)}
								aria-label="Close notifications"
								title="Close"
								className="flex h-7 w-7 items-center justify-center rounded text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
							>
								<X className="h-4 w-4" />
							</button>
						</div>
					</div>

					{active ? (
						<ul className="max-h-[28rem] overflow-y-auto py-1">
							{items.map(({ key, mr, activity }) => (
								<li
									key={key}
									className="border-b border-[var(--color-border)] px-3 py-2 last:border-b-0 hover:bg-[var(--color-bg-secondary)]"
								>
									<div className="flex min-w-0 items-start gap-2">
										<div className="min-w-0 flex-1">
											<NotificationLink
												mr={mr}
												onOpen={() => onMarkSeen?.(mr)}
											/>
											<p className="truncate text-xs text-[var(--color-text-secondary)]">
												{formatProjectName(mr.projectPath || "")}
											</p>
											<div className="mt-1 flex flex-wrap items-center gap-1.5">
												{activity.map((change, index) => {
													const { Icon: ChangeIcon, text } =
														describeChange(change);
													return (
														<span
															key={`${change.type}-${index}`}
															className="inline-flex items-center gap-1 rounded-full bg-[var(--color-accent-muted)] px-2 py-0.5 text-xs font-medium text-[var(--color-accent)]"
														>
															<ChangeIcon className="h-3 w-3" />
															{text}
														</span>
													);
												})}
											</div>
										</div>
										<button
											type="button"
											onClick={() => onMarkSeen?.(mr)}
											aria-label={`Mark ${mr.title} as seen`}
											title="Mark as seen"
											className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
										>
											<Check className="h-4 w-4" />
										</button>
									</div>
								</li>
							))}
						</ul>
					) : (
						<div className="px-3 py-4 text-sm text-[var(--color-text-secondary)]">
							No watched MR notifications
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default NotificationBell;
